import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useLocale } from '@/i18n/LocaleContext';
import { useCompanyServicesQuery } from '@/services/api/contentApi';
import type { CompanyService } from '@/services/api/types';

/**
 * The company's "what we offer" list. Renders nothing when the administrator
 * has not configured any services, the same way the reviews section hides
 * itself rather than showing an empty heading.
 */
export function ServicesSection() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { data: services, isLoading, isError } = useCompanyServicesQuery(locale);

  if (isLoading || isError || !services || services.length === 0) {
    return null;
  }

  return (
    <Box component="section" sx={{ py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          <Typography variant="h2" component="h2">
            {t('home.services.title')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            }}
          >
            {services.map((service, index) => (
              <ServiceCard key={`${service.name}-${index}`} service={service} />
            ))}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

function ServiceCard({ service }: { service: CompanyService }) {
  return (
    <Card sx={{ height: '100%' }}>
      {service.imageUrl && (
        <CardMedia component="img" height="140" image={service.imageUrl} alt={service.name} loading="lazy" />
      )}
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {service.name}
          </Typography>
          {service.description && (
            <Typography variant="body2" color="text.secondary">
              {service.description}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default ServicesSection;
